import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import { 
  getCommissionReports,
  getBusOwnerCommission,
  updateBusOwnerCommission,
  processCommissionPayout,
  getBusOwners,
  processCommissionPayment,
  getCommissionPaymentHistory,
  getAllCommissionPayments,
  updatePaymentStatus
} from '../../Services/Operations/ProductAPI';
import OwnerPaymentLogModal from './OwnerPaymentLogModal';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>Owner</th>
          <th>Commission Rate</th>
          <th>Total Commission</th>
          <th>Paid</th>
          <th>Pending</th>
          <th>Revenue</th>
          <th>Bookings</th>
          <th>Bank Details</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonPaymentModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal" style={{ width: '600px' }}>
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const OwnerCommission = () => {
  const { token } = useSelector(state => state.user);
  const [logModal, setLogModal] = useState({ show: false, ownerId: null, ownerName: '' });
  const [commissionData, setCommissionData] = useState({
    busOwners: [],
    overallStats: {},
    pagination: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [dateFilter, setDateFilter] = useState('All Time');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    minCommission: '',
    maxCommission: '',
    minRevenue: '',
    maxRevenue: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOwners, setTotalOwners] = useState(0);
  const [pageSize] = useState(10);
  
  const [viewingOwner, setViewingOwner] = useState(null);
  const [viewingBankDetails, setViewingBankDetails] = useState(null);
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionForm, setCommissionForm] = useState({});
  const [processingPayment, setProcessingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentMethod: 'bank_transfer',
    notes: '',
    reference: ''
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedOwnerForHistory, setSelectedOwnerForHistory] = useState(null);
  const [paymentHistoryPage, setPaymentHistoryPage] = useState(1);
  const [paymentHistoryTotal, setPaymentHistoryTotal] = useState(0);
  const [showAllPaymentsModal, setShowAllPaymentsModal] = useState(false);
  const [allPayments, setAllPayments] = useState([]);
  const [allPaymentsStats, setAllPaymentsStats] = useState({});
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCommissionData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, statusFilter, dateFilter, filters]);

  const handleViewPaymentLogs = (owner) => {
  setLogModal({
    show: true,
    ownerId: owner._id,
    ownerName: owner.name || 'Unknown Owner'
  });
};

  const fetchCommissionData = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      
      if (searchTerm) {
        filterParams.search = searchTerm;
      }
      
      if (statusFilter !== 'All Status') {
        filterParams.status = statusFilter.toLowerCase();
      }
      
      if (dateFilter !== 'All Time') {
        let startDate = new Date();
        let endDate = new Date();
        
        switch (dateFilter) {
          case 'This Month':
            startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
            break;
          case 'Last Month':
            startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1);
            endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
            break;
          case 'Last 3 Months':
            startDate.setMonth(startDate.getMonth() - 3);
            break;
          default:
            break;
        }
        
        if (dateFilter !== 'All Time') {
          filterParams.startDate = startDate.toISOString().split('T')[0];
          filterParams.endDate = endDate.toISOString().split('T')[0];
        }
      }
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          filterParams[key] = filters[key];
        }
      });

      const params = {
        page: currentPage,
        limit: pageSize,
        ...filterParams
      };

      console.log('Fetching commission data with params:', params);
      const response = await getCommissionReports(params, token);
      console.log("response getCommissionReports: ",response)
      if (response && response.success) {
        setCommissionData(response.data);
        
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalOwners(response.data.pagination.total || 0);
        }
        
        const hasActiveFilters = 
          searchTerm !== '' || 
          statusFilter !== 'All Status' || 
          dateFilter !== 'All Time' ||
          Object.values(filters).some(value => value !== '');
        setIsFiltered(hasActiveFilters);
      } else {
        setError(response?.message || 'Failed to fetch commission data');
        setCommissionData({ busOwners: [], overallStats: {}, pagination: {} });
      }
    } catch (err) {
      console.error('Error fetching commission data:', err);
      setError('Failed to load commission data. Please try again.');
      setCommissionData({ busOwners: [], overallStats: {}, pagination: {} });
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchCommissionData();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All Status');
    setDateFilter('All Time');
    setFilters({
      name: '',
      email: '',
      phone: '',
      minCommission: '',
      maxCommission: '',
      minRevenue: '',
      maxRevenue: '',
      dateFrom: '',
      dateTo: ''
    });
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleViewOwner = async (owner) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      const response = await getBusOwnerCommission(owner._id, token);
      
      if (response && response.success) {
        setViewingOwner({
          ...owner,
          commissionDetails: response.data
        });
      } else {
        throw new Error('Failed to fetch owner commission details');
      }
    } catch (err) {
      console.error('Error fetching owner details:', err);
      setError('Failed to load owner commission details');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditCommission = (owner) => {
    setEditingCommission(owner._id);
    setCommissionForm({
      commissionRate: owner.commissionRate || 10,
      commissionType: owner.commissionType || 'percentage',
      fixedCommission: owner.fixedCommission || 0
    });
  };

  const handleSaveCommission = async () => {
    try {
      setError(null);
      
      const commissionData = {
        commissionRate: parseFloat(commissionForm.commissionRate),
        commissionType: commissionForm.commissionType,
        fixedCommission: commissionForm.commissionType === 'fixed' ? parseFloat(commissionForm.fixedCommission) : 0
      };

      const response = await updateBusOwnerCommission(editingCommission, commissionData, token);
      
      if (response && response.success) {
        await fetchCommissionData();
        setEditingCommission(null);
        setCommissionForm({});
        alert('Commission settings updated successfully!');
      } else {
        throw new Error(response?.message || 'Failed to update commission');
      }
    } catch (err) {
      console.error('Error updating commission:', err);
      setError('Failed to update commission settings');
      alert('Failed to update commission settings. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommission(null);
    setCommissionForm({});
  };

  const handleProcessPayment = (owner) => {
    setProcessingPayment(owner);
    setIsPartialPayment(false);
    setPaymentForm({
      amount: owner.pendingCommission || 0,
      paymentMethod: 'bank_transfer',
      notes: '',
      reference: ''
    });
  };

  const fetchPaymentHistory = async (ownerId, page = 1) => {
    try {
      setLoadingPaymentHistory(true);
      const response = await getCommissionPaymentHistory(ownerId, token, page, 10);
      if (response && response.success) {
        setPaymentHistory(response.data.payments || []);
        setPaymentHistoryTotal(response.data.pagination?.total || 0);
        setPaymentHistoryPage(page);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setError('Failed to load payment history');
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  const handleViewPaymentHistory = async (owner) => {
    setSelectedOwnerForHistory(owner);
    setShowPaymentHistoryModal(true);
    await fetchPaymentHistory(owner._id, 1);
  };

  const handleViewAllPayments = async () => {
    try {
      setLoadingAllPayments(true);
      setShowAllPaymentsModal(true);
      const response = await getAllCommissionPayments(token, { limit: 50 });
      if (response && response.success) {
        setAllPayments(response.data.payments || []);
        setAllPaymentsStats(response.data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching all payments:', error);
      setError('Failed to load payment history');
    } finally {
      setLoadingAllPayments(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setError(null);
      
      let amountToPay = paymentForm.amount;
      
      if (!isPartialPayment) {
        amountToPay = processingPayment.pendingCommission;
      }
      
      if (!amountToPay || amountToPay <= 0) {
        alert('Please enter a valid payment amount');
        return;
      }
      
      if (amountToPay > processingPayment.pendingCommission) {
        alert(`Payment amount cannot exceed pending commission of ₹${processingPayment.pendingCommission.toLocaleString()}`);
        return;
      }
      
      const paymentData = {
        amount: amountToPay,
        paymentMethod: paymentForm.paymentMethod,
        notes: paymentForm.notes,
        reference: paymentForm.reference
      };
      
      const response = await processCommissionPayment(processingPayment._id, paymentData, token);
      
      if (response && response.success) {
        await fetchCommissionData();
        setProcessingPayment(null);
        setPaymentForm({
          amount: 0,
          paymentMethod: 'bank_transfer',
          notes: '',
          reference: ''
        });
        setIsPartialPayment(false);
        alert(`Commission payment of ₹${amountToPay.toLocaleString()} processed successfully!`);
      } else {
        throw new Error(response?.message || 'Failed to process payment');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Failed to process commission payment');
      alert(err.message || 'Failed to process payment. Please try again.');
    }
  };

  const handleBulkPayment = () => {
    const ownersWithPendingCommission = commissionData.busOwners.filter(
      owner => owner.pendingCommission > 0
    );

    if (ownersWithPendingCommission.length === 0) {
      alert('No bus owners have pending commissions!');
      return;
    }

    const totalAmount = ownersWithPendingCommission.reduce(
      (sum, owner) => sum + owner.pendingCommission, 0
    );

    if (window.confirm(`Process bulk payment for ${ownersWithPendingCommission.length} bus owners totaling ₹${totalAmount.toLocaleString()}?`)) {
      alert('Bulk payment feature would process payments for all selected owners. This would require additional backend implementation.');
    }
  };

  const handleExportReport = () => {
    if (commissionData.busOwners.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Owner Name', 'Email', 'Phone', 'Commission Rate', 'Total Commission', 'Paid Commission', 'Pending Commission', 'Total Revenue', 'Total Bookings', 'Bank Details'];
    const csvData = commissionData.busOwners.map(owner => [
      owner.name || 'N/A',
      owner.email || 'N/A',
      owner.phone || 'N/A',
      `${owner.commissionRate}%`,
      `₹${owner.totalCommissionEarned?.toLocaleString() || 0}`,
      `₹${owner.paidCommission?.toLocaleString() || 0}`,
      `₹${owner.pendingCommission?.toLocaleString() || 0}`,
      `₹${owner.stats?.totalRevenue?.toLocaleString() || 0}`,
      owner.stats?.totalBookings || 0,
      owner.bankDetails ? `${owner.bankDetails.bankName} - ${owner.bankDetails.accountNumber}` : 'No Bank Details'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `owner-commission-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    alert('Commission report exported successfully!');
  };

  const closeViewModal = () => setViewingOwner(null);
  const closeBankDetailsModal = () => setViewingBankDetails(null);
  const closePaymentModal = () => {
    setProcessingPayment(null);
    setPaymentForm({
      amount: 0,
      paymentMethod: 'bank_transfer',
      notes: '',
      reference: ''
    });
    setIsPartialPayment(false);
  };
  const closeAddModal = () => setShowAddModal(false);
  const closePaymentHistoryModal = () => {
    setShowPaymentHistoryModal(false);
    setSelectedOwnerForHistory(null);
    setPaymentHistory([]);
  };
  const closeAllPaymentsModal = () => {
    setShowAllPaymentsModal(false);
    setAllPayments([]);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    clearFilters();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const calculateStats = () => {
    const currentOwners = commissionData.busOwners;
    const totalCommission = currentOwners.reduce((sum, owner) => sum + (owner.totalCommissionEarned || 0), 0);
    const pendingCommission = currentOwners.reduce((sum, owner) => sum + (owner.pendingCommission || 0), 0);
    const paidCommission = currentOwners.reduce((sum, owner) => sum + (owner.paidCommission || 0), 0);
    const paidPercentage = totalCommission > 0 ? Math.round((paidCommission / totalCommission) * 100) : 0;
    
    return { totalCommission, pendingCommission, paidCommission, paidPercentage };
  };

  const stats = calculateStats();

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const getSafeString = (value) => value === null || value === undefined ? '' : String(value);
  const getSafeNumber = (value) => value === null || value === undefined ? 0 : Number(value);

  const renderBankDetailsBadge = (bankDetails) => {
    if (!bankDetails) {
      return <span className="status-badge pending" style={{ fontSize: '11px' }}>No Bank Details</span>;
    }
    
    return (
      <div>
        <span className="status-badge approved" style={{ fontSize: '11px', backgroundColor: bankDetails.isVerified ? '#48bb78' : '#ed8936' }}>
          {bankDetails.isVerified ? '✓ Verified' : '⚠ Pending'}
        </span>
        <div style={{ fontSize: '11px', color: '#718096', marginTop: '4px' }}>
          {bankDetails.bankName || 'No Bank'} - {bankDetails.accountNumber || 'No Account'}
        </div>
      </div>
    );
  };

  if (loading && commissionData.busOwners.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-select"></div>
            <div className="skeleton-select"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Owner Commission Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Search by owner name or email..." 
              className="search-input" 
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "300px" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="filter-btn" onClick={toggleFilters}>
            🔍 Filters
          </button>
          <select 
            className="filter-select" 
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option>All Status</option>
            <option>Pending</option>
            <option>Paid</option>
          </select>
          <select 
            className="filter-select" 
            value={dateFilter}
            onChange={handleDateFilterChange}
          >
            <option>All Time</option>
            <option>This Month</option>
            <option>Last Month</option>
            <option>Last 3 Months</option>
          </select>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportReport}>
            Export Report
          </button>
          <button 
            className="add-btn" 
            onClick={handleViewAllPayments}
            style={{backgroundColor: '#4299e1'}}
          >
            View All Payments
          </button>
        </div>
      </div>


      {showFilters && !loading && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Commission Data</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Owner Name</label>
              <input
                type="text"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                placeholder="Enter owner name"
              />
            </div>
            <div className="filter-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                placeholder="Enter email"
              />
            </div>
            <div className="filter-group">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={filters.phone}
                onChange={handleFilterChange}
                placeholder="Enter phone"
              />
            </div>
            <div className="filter-group">
              <label>Min Commission Rate (%)</label>
              <input
                type="number"
                name="minCommission"
                value={filters.minCommission}
                onChange={handleFilterChange}
                placeholder="Minimum commission"
                min="0"
                max="100"
              />
            </div>
            <div className="filter-group">
              <label>Max Commission Rate (%)</label>
              <input
                type="number"
                name="maxCommission"
                value={filters.maxCommission}
                onChange={handleFilterChange}
                placeholder="Maximum commission"
                min="0"
                max="100"
              />
            </div>
            <div className="filter-group">
              <label>Min Revenue</label>
              <input
                type="number"
                name="minRevenue"
                value={filters.minRevenue}
                onChange={handleFilterChange}
                placeholder="Minimum revenue"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Revenue</label>
              <input
                type="number"
                name="maxRevenue"
                value={filters.maxRevenue}
                onChange={handleFilterChange}
                placeholder="Maximum revenue"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {statusFilter !== 'All Status' && (
                <span className="filter-tag">Status: {statusFilter}</span>
              )}
              {dateFilter !== 'All Time' && (
                <span className="filter-tag">Date: {dateFilter}</span>
              )}
              {Object.entries(filters).map(([key, value]) => 
                value && (
                  <span key={key} className="filter-tag">
                    {key}: {value}
                  </span>
                )
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Owners</h3>
          <p>{totalOwners}</p>
          <span>All registered owners</span>
        </div>
        <div className="stat-card">
          <h3>Total Commission</h3>
          <p>₹{stats.totalCommission.toLocaleString()}</p>
          <span>all time earnings</span>
        </div>
        <div className="stat-card">
          <h3>Paid Out</h3>
          <p>₹{stats.paidCommission.toLocaleString()}</p>
          <span>{stats.paidPercentage}% paid out</span>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p>₹{stats.pendingCommission.toLocaleString()}</p>
          <span>to be paid</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{commissionData.busOwners.length}</p>
          <span>owners found</span>
        </div>
      </div>

      {commissionData.overallStats && (
        <div className="overall-stats" style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #e9ecef'
        }}>
          <h4>Overall Commission Statistics</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div>
              <strong>Total Revenue:</strong> ₹{getSafeNumber(commissionData.overallStats.totalRevenue).toLocaleString()}
            </div>
            <div>
              <strong>Total Commission:</strong> ₹{getSafeNumber(commissionData.overallStats.totalCommission).toLocaleString()}
            </div>
            <div>
              <strong>Average Commission Rate:</strong> {getSafeNumber(commissionData.overallStats.commissionRate).toFixed(2)}%
            </div>
            {commissionData.overallStats.bankDetails && (
              <div>
                <strong>Bank Details:</strong> {commissionData.overallStats.bankDetails.totalWithBankDetails} verified / {commissionData.overallStats.bankDetails.pendingVerification} pending
              </div>
            )}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Owner</th>
              <th>Commission Rate</th>
              <th>Total Commission</th>
              <th>Paid</th>
              <th>Pending</th>
              <th>Revenue</th>
              <th>Bookings</th>
              <th>Bank Details</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {commissionData.busOwners.length > 0 ? (
              commissionData.busOwners.map(owner => (
                <tr key={owner._id}>
                  <td>
                    <div>
                      <div>{getSafeString(owner.name)}</div>
                      <div className="phone">{getSafeString(owner.email)}</div>
                      <div className="phone">{getSafeString(owner.phone)}</div>
                    </div>
                  </td>
                  <td>
                    {editingCommission === owner._id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <select
                          value={commissionForm.commissionType}
                          onChange={(e) => setCommissionForm(prev => ({...prev, commissionType: e.target.value}))}
                          className="edit-select"
                          style={{ width: '100%' }}
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed</option>
                        </select>
                        {commissionForm.commissionType === 'percentage' ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={commissionForm.commissionRate}
                            onChange={(e) => setCommissionForm(prev => ({...prev, commissionRate: e.target.value}))}
                            className="edit-input"
                            placeholder="Commission Rate %"
                          />
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={commissionForm.fixedCommission}
                            onChange={(e) => setCommissionForm(prev => ({...prev, fixedCommission: e.target.value}))}
                            className="edit-input"
                            placeholder="Fixed Commission"
                          />
                        )}
                      </div>
                    ) : (
                      <div>
                        <div>{getSafeNumber(owner.commissionRate)}%</div>
                        <div className="phone" style={{color: '#718096', fontSize: '11px'}}>
                          {getSafeString(owner.commissionType)}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <div>
                      <div>₹{getSafeNumber(owner.totalCommissionEarned).toLocaleString()}</div>
                      <div className="phone" style={{color: '#718096', fontSize: '11px'}}>
                        Total earned
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#38a169' }}>
                      <div>₹{getSafeNumber(owner.paidCommission).toLocaleString()}</div>
                      <div className="phone" style={{fontSize: '11px'}}>
                        Paid amount
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: owner.pendingCommission > 0 ? '#e53e3e' : '#38a169' }}>
                      <div>₹{getSafeNumber(owner.pendingCommission).toLocaleString()}</div>
                      <div className="phone" style={{fontSize: '11px'}}>
                        To be paid
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>₹{getSafeNumber(owner.stats?.totalRevenue).toLocaleString()}</div>
                      <div className="phone" style={{color: '#718096', fontSize: '11px'}}>
                        Total revenue
                      </div>
                    </div>
                  </td>
                  <td>{getSafeNumber(owner.stats?.totalBookings)}</td>
                  <td>
                    {owner.bankDetails ? (
                      <button 
                        className="action-btn view" 
                        onClick={() => setViewingBankDetails(owner)}
                        style={{ 
                          backgroundColor: '#4299e1', 
                          fontSize: '11px', 
                          padding: '4px 8px',
                          margin: '0'
                        }}
                      >
                        View Bank Details
                      </button>
                    ) : (
                      <span className="status-badge pending" style={{ fontSize: '11px' }}>
                        Not Added
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${owner.pendingCommission > 0 ? 'pending' : 'paid'}`}>
                      {owner.pendingCommission > 0 ? 'Pending' : 'Paid'}
                    </span>
                  </td>
                  <td>
                    {editingCommission === owner._id ? (
                      <div className="edit-actions">
                        <button className="action-btn save" onClick={handleSaveCommission}>
                          Save
                        </button>
                        <button className="action-btn cancel" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button 
                          className="action-btn view" 
                          onClick={() => handleViewOwner(owner)}
                        >
                          Details
                        </button>
                        <button 
                          className="action-btn edit" 
                          onClick={() => handleEditCommission(owner)}
                        >
                          Edit Rate
                        </button>
                        <button 
                          className="action-btn history" 
                          onClick={() => handleViewPaymentHistory(owner)}
                          style={{backgroundColor: '#4299e1'}}
                        >
                          History
                        </button>
                         <button 
      className="action-btn log" 
      onClick={() => handleViewPaymentLogs(owner)}
      style={{
        backgroundColor: '#6f42c1',
        color: 'white'
      }}
    >
      📋 Logs
    </button>
                        {owner.pendingCommission > 0 && (
                          <button 
                            className="action-btn pay" 
                            onClick={() => handleProcessPayment(owner)}
                          >
                            Pay
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-data">
                  <p>No bus owners found {isFiltered ? 'matching your filters' : ''}.</p>
                  {isFiltered && (
                    <button className="clear-filters-link" onClick={clearFilters}>
                      Clear filters to see all owners
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {commissionData.busOwners.length} of {totalOwners} bus owners
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          {isFiltered && !searchTerm && <span className="filtered-indicator"> • Filtered Results</span>}
        </div>
        <div className="pagination-controls">
          <button 
            className="pagination-btn" 
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {generatePageNumbers().map(pageNumber => (
              <button
                key={pageNumber}
                className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                onClick={() => handlePageClick(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="page-dots">...</span>
                <button
                  className="page-number"
                  onClick={() => handlePageClick(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          
          <button 
            className="pagination-btn" 
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingOwner && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Commission Details - {viewingOwner.name}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Owner Name:</label>
                  <span>{getSafeString(viewingOwner.name)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{getSafeString(viewingOwner.email)}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{getSafeString(viewingOwner.phone)}</span>
                </div>
                <div className="detail-row">
                  <label>Commission Rate:</label>
                  <span>{getSafeNumber(viewingOwner.commissionRate)}% ({getSafeString(viewingOwner.commissionType)})</span>
                </div>
                <div className="detail-row">
                  <label>Total Commission Earned:</label>
                  <span style={{color: '#2f92c4', fontWeight: '600'}}>
                    ₹{getSafeNumber(viewingOwner.totalCommissionEarned).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Paid Commission:</label>
                  <span style={{color: '#38a169', fontWeight: '600'}}>
                    ₹{getSafeNumber(viewingOwner.paidCommission).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Pending Commission:</label>
                  <span style={{color: '#e53e3e', fontWeight: '600'}}>
                    ₹{getSafeNumber(viewingOwner.pendingCommission).toLocaleString()}
                  </span>
                </div>
                
                {viewingOwner.bankDetails && (
                  <>
                    <div className="detail-row" style={{ marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                      <label style={{ fontWeight: 'bold', fontSize: '16px' }}>Bank Details:</label>
                      <span></span>
                    </div>
                    <div className="detail-row">
                      <label>Account Holder Name:</label>
                      <span>{viewingOwner.bankDetails.accountHolderName || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Bank Name:</label>
                      <span>{viewingOwner.bankDetails.bankName || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Account Number:</label>
                      <span style={{ fontFamily: 'monospace' }}>{viewingOwner.bankDetails.accountNumber || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <label>IFSC Code:</label>
                      <span>{viewingOwner.bankDetails.ifscCode || '-'}</span>
                    </div>
                    <div className="detail-row">
                      <label>UPI ID:</label>
                      <span>{viewingOwner.bankDetails.upiId || '-'}</span>
                    </div>
                    
                    {viewingOwner.bankDetails.verifiedAt && (
                      <div className="detail-row">
                        <label>Verified On:</label>
                        <span>{new Date(viewingOwner.bankDetails.verifiedAt).toLocaleString()}</span>
                      </div>
                    )}
                  </>
                )}
                
                {viewingOwner.commissionDetails && (
                  <>
                    <div className="detail-row">
                      <label>Total Bookings:</label>
                      <span>{getSafeNumber(viewingOwner.commissionDetails.commissionStats?.totalBookings)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Total Revenue:</label>
                      <span>₹{getSafeNumber(viewingOwner.commissionDetails.commissionStats?.totalRevenue).toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <label>Calculated Commission:</label>
                      <span>₹{getSafeNumber(viewingOwner.commissionDetails.commissionStats?.calculatedCommission).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingOwner && isFetchingDetails && <SkeletonModal />}


      {viewingBankDetails && (
        <div className="modal-overlay" onClick={closeBankDetailsModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Bank Details - {viewingBankDetails.name}</h3>
              <button className="modal-close-btn" onClick={closeBankDetailsModal}>×</button>
            </div>
            <div className="modal-body">
              {viewingBankDetails.bankDetails ? (
                <div className="customer-details">
                  <div className="detail-row">
                    <label>Owner Name:</label>
                    <span><strong>{viewingBankDetails.name}</strong></span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{viewingBankDetails.email || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Phone:</label>
                    <span>{viewingBankDetails.phone}</span>
                  </div>
                  <div className="detail-row" style={{ marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                    <label style={{ fontWeight: 'bold' }}>Account Details:</label>
                    <span></span>
                  </div>
                  <div className="detail-row">
                    <label>Account Holder Name:</label>
                    <span><strong>{viewingBankDetails.bankDetails.accountHolderName || '-'}</strong></span>
                  </div>
                  <div className="detail-row">
                    <label>Bank Name:</label>
                    <span>{viewingBankDetails.bankDetails.bankName || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Account Number:</label>
                    <span style={{ fontSize: '16px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {viewingBankDetails.bankDetails.accountNumber || '-'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>IFSC Code:</label>
                    <span style={{ fontFamily: 'monospace' }}>{viewingBankDetails.bankDetails.ifscCode || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <label>UPI ID:</label>
                    <span>{viewingBankDetails.bankDetails.upiId || '-'}</span>
                  </div>
                 
                  {viewingBankDetails.bankDetails.verifiedAt && (
                    <div className="detail-row">
                      <label>Verified On:</label>
                      <span>{new Date(viewingBankDetails.bankDetails.verifiedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {viewingBankDetails.bankDetails.createdAt && (
                    <div className="detail-row">
                      <label>Added On:</label>
                      <span>{new Date(viewingBankDetails.bankDetails.createdAt).toLocaleString()}</span>
                    </div>
                  )}
                  {viewingBankDetails.bankDetails.updatedAt && 
                   viewingBankDetails.bankDetails.updatedAt !== viewingBankDetails.bankDetails.createdAt && (
                    <div className="detail-row">
                      <label>Last Updated:</label>
                      <span>{new Date(viewingBankDetails.bankDetails.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p>No bank details available for this owner.</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeBankDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {processingPayment && (
        <div className="modal-overlay" onClick={closePaymentModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ width: '600px', maxWidth: '90%' }}>
            <div className="modal-header">
              <h3>Process Commission Payment</h3>
              <button className="modal-close-btn" onClick={closePaymentModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Owner:</label>
                  <span>{getSafeString(processingPayment.name)}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{getSafeString(processingPayment.email)}</span>
                </div>
                <div className="detail-row">
                  <label>Phone:</label>
                  <span>{getSafeString(processingPayment.phone)}</span>
                </div>
                
                {processingPayment.bankDetails && (
                  <div className="detail-row" style={{ marginTop: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                    <label>Bank Account:</label>
                    <span>
                      {processingPayment.bankDetails.bankName} - 
                      {processingPayment.bankDetails.accountNumber}
                      {!processingPayment.bankDetails.isVerified && (
                        <span style={{ color: '#ed8936', marginLeft: '8px' }}>(Not Verified)</span>
                      )}
                    </span>
                  </div>
                )}
                
                <div className="detail-row">
                  <label>Total Commission Earned:</label>
                  <span style={{color: '#2f92c4', fontWeight: '600'}}>
                    ₹{getSafeNumber(processingPayment.totalCommissionEarned).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Already Paid:</label>
                  <span style={{color: '#38a169', fontWeight: '600'}}>
                    ₹{getSafeNumber(processingPayment.paidCommission).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Pending Commission:</label>
                  <span style={{color: '#e53e3e', fontWeight: '600'}}>
                    ₹{getSafeNumber(processingPayment.pendingCommission).toLocaleString()}
                  </span>
                </div>
                
                <div className="detail-row">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={isPartialPayment}
                      onChange={(e) => {
                        setIsPartialPayment(e.target.checked);
                        if (!e.target.checked) {
                          setPaymentForm(prev => ({...prev, amount: processingPayment.pendingCommission}));
                        } else {
                          setPaymentForm(prev => ({...prev, amount: 0}));
                        }
                      }}
                    />
                    {' '}Partial Payment
                  </label>
                </div>
                
                <div className="detail-row">
                  <label>Amount to Pay:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="number"
                      min="0"
                      max={processingPayment.pendingCommission}
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => {
                        let value = parseFloat(e.target.value);
                        if (isNaN(value)) value = 0;
                        if (value > processingPayment.pendingCommission) {
                          value = processingPayment.pendingCommission;
                        }
                        if (value < 0) value = 0;
                        setPaymentForm(prev => ({...prev, amount: value}));
                      }}
                      className="edit-input"
                      style={{ width: '200px' }}
                      disabled={!isPartialPayment}
                    />
                    {!isPartialPayment && (
                      <small style={{ color: '#718096' }}>
                        Full amount will be paid (₹{processingPayment.pendingCommission.toLocaleString()})
                      </small>
                    )}
                  </div>
                  {isPartialPayment && paymentForm.amount > processingPayment.pendingCommission && (
                    <small style={{ color: '#e53e3e', display: 'block', marginTop: '5px' }}>
                      Amount cannot exceed pending commission of ₹{processingPayment.pendingCommission.toLocaleString()}
                    </small>
                  )}
                  {isPartialPayment && paymentForm.amount <= 0 && (
                    <small style={{ color: '#e53e3e', display: 'block', marginTop: '5px' }}>
                      Please enter a valid amount greater than 0
                    </small>
                  )}
                </div>
                
                <div className="detail-row">
                  <label>Payment Method:</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm(prev => ({...prev, paymentMethod: e.target.value}))}
                    className="edit-select"
                    style={{ width: '200px' }}
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                
                <div className="detail-row">
                  <label>Reference/Transaction ID:</label>
                  <input
                    type="text"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm(prev => ({...prev, reference: e.target.value}))}
                    className="edit-input"
                    placeholder="Optional reference number"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div className="detail-row">
                  <label>Notes:</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({...prev, notes: e.target.value}))}
                    className="edit-input"
                    placeholder="Optional notes"
                    rows="3"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={closePaymentModal}>
                Cancel
              </button>
              <button 
                className="action-btn pay" 
                onClick={handleConfirmPayment}
                disabled={
                  processingPayment.pendingCommission === 0 ||
                  (isPartialPayment && (!paymentForm.amount || paymentForm.amount <= 0 || paymentForm.amount > processingPayment.pendingCommission))
                }
              >
                {isPartialPayment 
                  ? `Pay ₹${paymentForm.amount.toLocaleString()}` 
                  : `Pay Full ₹${processingPayment.pendingCommission.toLocaleString()}`
                }
              </button>
            </div>
          </div>
        </div>
      )}


      {showPaymentHistoryModal && selectedOwnerForHistory && (
        <div className="modal-overlay" onClick={closePaymentHistoryModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ width: '800px', maxWidth: '90%' }}>
            <div className="modal-header">
              <h3>Payment History - {selectedOwnerForHistory.name}</h3>
              <button className="modal-close-btn" onClick={closePaymentHistoryModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Total Commission:</label>
                  <span>₹{getSafeNumber(selectedOwnerForHistory.totalCommissionEarned).toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <label>Total Paid:</label>
                  <span style={{color: '#38a169'}}>₹{getSafeNumber(selectedOwnerForHistory.paidCommission).toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <label>Pending:</label>
                  <span style={{color: '#e53e3e'}}>₹{getSafeNumber(selectedOwnerForHistory.pendingCommission).toLocaleString()}</span>
                </div>
              </div>
              
              {loadingPaymentHistory ? (
                <p>Loading payment history...</p>
              ) : paymentHistory.length > 0 ? (
                <div style={{ marginTop: '20px' }}>
                  <h4>Payment Transactions</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Amount</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Method</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Reference</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map(payment => (
                        <tr key={payment._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px' }}>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                          <td style={{ padding: '10px', fontWeight: '600' }}>₹{payment.amount.toLocaleString()}</td>
                          <td style={{ padding: '10px', textTransform: 'capitalize' }}>{payment.paymentMethod}</td>
                          <td style={{ padding: '10px' }}>{payment.reference || '-'}</td>
                          <td style={{ padding: '10px' }}>
                            <span className={`status-badge ${payment.status}`}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {paymentHistoryTotal > 10 && (
                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                      <button 
                        className="pagination-btn"
                        onClick={() => fetchPaymentHistory(selectedOwnerForHistory._id, paymentHistoryPage + 1)}
                        disabled={paymentHistoryPage * 10 >= paymentHistoryTotal}
                      >
                        Load More
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ textAlign: 'center', marginTop: '20px' }}>No payment history found</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closePaymentHistoryModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {showAllPaymentsModal && (
        <div className="modal-overlay" onClick={closeAllPaymentsModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ width: '1000px', maxWidth: '95%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>All Commission Payments</h3>
              <button className="modal-close-btn" onClick={closeAllPaymentsModal}>×</button>
            </div>
            <div className="modal-body">
              {allPaymentsStats && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: '15px', 
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  <div>
                    <strong>Total Payments:</strong>
                    <p style={{ fontSize: '20px', color: '#2f92c4' }}>₹{getSafeNumber(allPaymentsStats.totalAmount).toLocaleString()}</p>
                  </div>
                  <div>
                    <strong>Completed:</strong>
                    <p style={{ fontSize: '20px', color: '#38a169' }}>₹{getSafeNumber(allPaymentsStats.completedAmount).toLocaleString()}</p>
                  </div>
                  <div>
                    <strong>Pending:</strong>
                    <p style={{ fontSize: '20px', color: '#e53e3e' }}>₹{getSafeNumber(allPaymentsStats.pendingAmount).toLocaleString()}</p>
                  </div>
                  <div>
                    <strong>Transactions:</strong>
                    <p style={{ fontSize: '20px' }}>{getSafeNumber(allPaymentsStats.totalCount)}</p>
                  </div>
                </div>
              )}
              
              {loadingAllPayments ? (
                <p>Loading payments...</p>
              ) : allPayments.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Owner</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Amount</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Method</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Reference</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPayments.map(payment => (
                      <tr key={payment._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px' }}>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                        <td style={{ padding: '10px' }}>
                          <div>{payment.busOwner?.name || 'N/A'}</div>
                          <small style={{ color: '#718096' }}>{payment.busOwner?.email}</small>
                        </td>
                        <td style={{ padding: '10px', fontWeight: '600' }}>₹{payment.amount.toLocaleString()}</td>
                        <td style={{ padding: '10px', textTransform: 'capitalize' }}>{payment.paymentMethod}</td>
                        <td style={{ padding: '10px' }}>{payment.reference || '-'}</td>
                        <td style={{ padding: '10px' }}>
                          <span className={`status-badge ${payment.status}`}>
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ textAlign: 'center' }}>No payments found</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeAllPaymentsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {logModal.show && (
  <OwnerPaymentLogModal
    ownerId={logModal.ownerId}
    ownerName={logModal.ownerName}
    onClose={() => setLogModal({ show: false, ownerId: null, ownerName: '' })}
    token={token}
  />
)}
    </div>
  );
};

export default OwnerCommission;